// Pure data types describing a parsed PSD file.
//
// At this stage the parser only walks the structure of the file and records
// offsets/lengths for the heavy sections (channel image data, additional
// layer info blocks).  Decompression and pixel decode are explicit follow-up
// work — see ./README.md for the boundaries.
//
// PSD is big-endian.  All integers below are interpreted big-endian by the
// parser even though TypeScript itself has no notion of endianness.

/** Bit depth of a PSD file.  Photoshop only accepts these values. */
export type PsdDepth = 1 | 8 | 16 | 32;

/** Version flag from the file header. */
export type PsdVersion = 1 | 2; // 1 = PSD, 2 = PSB (large document)

/**
 * Color mode constants as written by Photoshop.  We do not narrow this to a
 * union because the spec carves out room for future modes and round-trip
 * fidelity requires preserving unknown values verbatim.
 */
export const PsdColorMode = {
  Bitmap: 0,
  Grayscale: 1,
  Indexed: 2,
  RGB: 3,
  CMYK: 4,
  Multichannel: 7,
  Duotone: 8,
  Lab: 9,
} as const;

export interface PsdHeader {
  version: PsdVersion;
  /** Number of channels in the document (1–56), including alpha. */
  channels: number;
  /** Document height in pixels (1–30000 for PSD, 1–300000 for PSB). */
  height: number;
  /** Document width in pixels. */
  width: number;
  depth: PsdDepth;
  /** Raw color mode value — see {@link PsdColorMode}. */
  colorMode: number;
}

/** A single Image Resource block (`8BIM` records, section 3). */
export interface PsdResource {
  /** Resource ID; see Adobe spec table "Image resource IDs". */
  id: number;
  /** Pascal-string name; usually empty. */
  name: string;
  /** Raw resource data, not interpreted at this stage. */
  data: Uint8Array;
}

/**
 * Channel descriptor from a layer record.  The actual channel pixels live
 * later in the file and are referenced via {@link PsdLayerRecord.channelImageOffset}.
 */
export interface PsdChannelInfo {
  /**
   * Channel ID.  0/1/2 = RGB, 3 = alpha for RGB docs, and the negative IDs
   * indicate masks:
   *   -1 = transparency mask
   *   -2 = user-supplied layer mask
   *   -3 = real user-supplied layer mask (when both kinds exist)
   */
  id: number;
  /** Length in bytes of this channel's data inside the channel-image area. */
  length: number;
}

/**
 * Raw (unparsed) Additional Layer Information block.  Keys like `lyid`,
 * `luni`, `lsct`, `lfx2`, etc. live here.  The next agent can dispatch on
 * `key` to parse the interesting ones.
 */
export interface PsdAdditionalLayerInfo {
  /** Either `8BIM` or `8B64` (the latter is used inside PSB documents). */
  signature: string;
  /** 4-character key, e.g. "luni" for Unicode layer name. */
  key: string;
  /** Raw payload bytes; length is exact (no trailing padding). */
  data: Uint8Array;
}

export interface PsdLayerRecord {
  top: number;
  left: number;
  bottom: number;
  right: number;
  channelCount: number;
  channels: PsdChannelInfo[];
  /** 4-char blend mode key, e.g. "norm", "mul ", "scrn". */
  blendModeKey: string;
  /** 0–255. */
  opacity: number;
  /** Clipping: 0 = base, 1 = non-base. */
  clipping: number;
  /** Bit flags from the layer record. */
  flags: number;
  /** Layer name from the legacy Pascal string slot (MacRoman, padded to 4). */
  name: string;
  /**
   * Raw bytes of the layer mask / adjustment data sub-block.  Decoded later;
   * empty `Uint8Array` if the layer has no mask data.
   */
  maskData: Uint8Array;
  /**
   * Raw bytes of the layer blending ranges sub-block.  Same deal as
   * {@link maskData} — preserved for the future decoder.
   */
  blendingRangesData: Uint8Array;
  /**
   * Byte offset (within the original PSD buffer) where this layer's channel
   * image data starts.  Each channel begins with a 2-byte compression code
   * followed by `channels[i].length - 2` payload bytes.  The next agent will
   * use this to drive the RLE / ZIP decoders.
   */
  channelImageOffset: number;
  /** Additional Layer Information blocks appearing inside this layer record. */
  additionalInfo: PsdAdditionalLayerInfo[];
}

export interface PsdLayerInfo {
  layerCount: number;
  layers: PsdLayerRecord[];
  /**
   * Some PSDs encode a negative layer count meaning "first alpha channel
   * holds transparency for the merged result".  Preserved verbatim for
   * writers.
   */
  layerCountRaw: number;
  /**
   * Global Layer Mask Info block, raw bytes.  Almost always empty.
   */
  globalLayerMaskData: Uint8Array;
  /**
   * Additional Layer Information blocks that appear *after* the layer list,
   * at the document level (e.g. `Lr16`, `Lr32`, `Patt`, …).
   */
  additionalInfo: PsdAdditionalLayerInfo[];
}

export interface PsdFile {
  header: PsdHeader;
  /** Section 2 raw bytes.  Empty for most RGB documents. */
  colorModeData: Uint8Array;
  /** Section 3, parsed into individual `8BIM` records. */
  imageResources: PsdResource[];
  /** Section 4, parsed down to layer records but with raw channel data. */
  layerInfo: PsdLayerInfo;
  /**
   * Byte offset of the start of section 5 (the merged composite image data).
   * The next agent will decode from here.
   */
  imageDataOffset: number;
}
